<template lang="pug">
.budget-limits-fields
  .inline-form-row
    ValidationProvider(
      v-slot="{ errors }"
      :rules="requiredField"
      name="type"
      slim
    )
      .form-col.form-type-select(:class="{ 'has-error': errors[0] }")
        label.string.required.control-label
          | Type
          abbr(title="required") *
        AppSelect#budget-attributes-type(
          :select2-options="typeSelect2Options"
          :options="budgetTypeOptions"
          :value="budgetType"
          :disabled="disabled"
          @input="$emit('change-budget-field', 'type', $event)"
        )
        AppDialogError(:errors="errors")
    ValidationProvider(
      v-if="!isBudgetTypeHours"
      v-slot="{ errors }"
      :rules="requiredField"
      name="rate"
      slim
    )
      .form-col.flex-1(:class="{ 'has-error': errors[0] }")
        label.string.required.control-label
          | Based on
          abbr(title="required") *
        AppSelect#budget-attributes-rate(
          :select2-options="rateSelect2Options"
          :options="budgetUseOptions"
          :value="budgetRate"
          :disabled="disabled"
          @input="$emit('change-budget-field', 'rate', $event)"
        )
        AppDialogError(:errors="errors")
    ValidationProvider(
      v-if="!isBudgetTypeHours"
      v-slot="{ errors }"
      :rules="`${requiredField}|greaterThanZero|max_value:${budgetLimit}`"
      name="cost"
      slim
    )
      .form-col.form-col-last
        .form-group.string.required.project_budget_cost(:class="{ 'has-error': errors[0] }")
          label.string.required.control-label
            | Cost
            abbr(title="required") *
          .input-group.col-sm-12
            span.input-group-addon {{ budgetCurrency }}
            input#budget-attributes-cost.form-control(
              type="text"
              placeholder="0.0"
              :value="budgetCost"
              :disabled="disabled"
              @input="$emit('change-budget-field', 'cost', $event.target.value)"
            )
          AppDialogError(:errors="errors")
    AppDurationInput(
      v-if="isBudgetTypeHours"
      with-seconds="never"
      :placeholder="formattedPlaceholder"
      label="Hours"
      required
      greater-than-zero
      :value="budgetHours"
      :disabled="disabled"
      @input="$emit('change-budget-field', 'hours_display', $event)"
    )
  template(v-if="!onlyBatchFields")
    .row.form-group
      .col-sm-6
        ValidationProvider(
          v-slot="{ errors }"
          name="notification"
          rules="between:1,200|lessThanStopTimer:@stop_at"
        )
          div(:class="{ 'has-error': errors[0] }")
            label.control-label.optional
              | Notify at
            .input-group.col-sm-12
              input#budget-attributes-notification.numeric.decimal.form-control(
                type="text"
                inputmode="numeric"
                placeholder="80"
                :value="budgetNotification"
                :disabled="disabled || !requiredFieldsPresent"
                @input="$emit('change-budget-field', 'alert.near_limit', $event.target.value)"
              )
              span.input-group-addon % of budget
            AppDialogError(:errors="errors")
      .col-sm-6(v-if="allowStopTimer")
        ValidationProvider(
          v-slot="{ errors }"
          name="stop_at"
          rules="between:1,9999"
        )
          div(:class="{ 'has-error': errors[0] }")
            label.control-label.optional
              | Stop timers at
            i.hi.hi-info.hi-12.a-tooltip(
              v-tooltip="stopAtTooltipOptions"
              :title="stopAtTooltip"
            )
            .input-group.col-sm-12
              input#budget-attributes-stop-timer.numeric.decimal.form-control(
                type="text"
                inputmode="numeric"
                placeholder="90"
                :value="budgetStopTimer"
                :disabled="disabled || !requiredFieldsPresent"
                @input="$emit('change-budget-field', 'stop_timer.limit', $event.target.value)"
              )
              span.input-group-addon % of budget
            AppDialogError(:errors="errors")
    .row.form-group.budget-controls(v-if="allowNotifyAllMembers")
      .notify-all-members-switch
        ValidationProvider(name="notifyAllMembers")
          label#budget-attributes-notify-all-members-switch.toggle-switch-wrapper
            input(
              type="checkbox"
              :value="budgetNotifyAllMembers"
              :checked="budgetNotifyAllMembers"
              :disabled="disabled || !requiredFieldsPresent"
              @input="$emit('change-budget-field', 'alert.all_members', !budgetNotifyAllMembers)"
            )
            .toggle-switch
            span.toggle-switch-label Notify project members
    BudgetRecurrenceRow(
      :disabled="disabled || !requiredFieldsPresent"
      :extended-reset-options="extendedResetOptions"
      :budget-state="budgetState"
      @change-budget-field="handleChangeBudgetField"
    )
    .row.budget-controls
      .billable-switch
        ValidationProvider(name="includeNonBillable")
          label#budget-attributes-include-non-billable.toggle-switch-wrapper
            input(
              v-if="budgetNotCleared"
              type="checkbox"
              :value="budgetIncludeNonBillable"
              :checked="budgetIncludeNonBillable"
              :disabled="disabled || !requiredFieldsPresent"
              @input="$emit('change-budget-field', 'include_non_billable', !budgetIncludeNonBillable)"
            )
            input(
              v-else
              type="checkbox"
              :checked="true"
              :disabled="disabled"
            )
            .toggle-switch
            span.toggle-switch-label Include non-billable time

      .remove-budget-link(
        v-if="!disabled && canClearBudget"
        @click="$emit('clear-budget')"
      )
        i.hi.hi-delete
        div Clear budget
</template>

<script>
import {
  BUDGET_TYPE_HOURS,
  BUDGET_TYPE_COST,
  BUDGET_USE_BILL_RATE,
  BUDGET_USE_PAY_RATE,
  BUDGET_TYPE_OPTIONS,
  BUDGET_USE_OPTIONS,
} from '~/app_constants';
import { BUDGET_LIMIT } from '~/server_env';
import { omit, pick } from 'lodash-es';
import { extend } from 'vee-validate';
import { BUDGET_FORM_KEYS } from '~/utils/budget_utils';
import BudgetRecurrenceRow from './BudgetRecurrenceRow.vue';

extend('lessThanStopTimer', {
  params: ['target'],
  validate(value, { target }) {
    if (!target) {
      return true;
    }

    return Number(value) < Number(target);
  },
  message: 'must be less than the "Stop Timers at" value',
});

export default {
  name: 'BudgetLimitsForm',
  components: { BudgetRecurrenceRow },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    budgetState: {
      type: Object,
      required: true,
    },
    hasBudget: {
      type: Boolean,
      required: true,
    },
    organization: {
      type: Object,
      required: true,
    },
    durationFormat: {
      type: String,
      default: 'h:mm',
    },
    allowStopTimer: {
      type: Boolean,
      default: false,
    },
    allowNotifyAllMembers: {
      type: Boolean,
      default: false,
    },
    clearedFields: {
      type: Boolean,
      default: false,
    },
    extendedResetOptions: {
      type: Boolean,
      default: false,
    },
    onlyBatchFields: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      defaultSelect2Options: {
        multiple: false,
        width: '100%',
        minimumResultsForSearch: -1,
      },
      stopAtTooltip:
        'Timers may not stop at this exact %, as multiple members can track time to the same project, sometimes on multiple apps. Their timers will stop within a short range of this value.',
      stopAtTooltipOptions: { placement: 'top' },
    };
  },
  computed: {
    budgetType() {
      return this.budgetState.type;
    },
    budgetRate() {
      return this.budgetState.rate;
    },
    budgetHours() {
      return this.budgetState.hours_display;
    },
    budgetCost() {
      return this.budgetState.cost;
    },
    budgetNotification() {
      return this.budgetState.alert ? this.budgetState.alert.near_limit : 80;
    },
    budgetStopTimer() {
      return this.budgetState.stop_timer ? this.budgetState.stop_timer.limit : 90;
    },
    budgetNotifyAllMembers() {
      return this.budgetState.alert ? this.budgetState.alert.all_members : false;
    },
    budgetIncludeNonBillable() {
      return this.budgetState.include_non_billable;
    },
    budgetTypeOptions() {
      return BUDGET_TYPE_OPTIONS;
    },
    budgetUseOptions() {
      return BUDGET_USE_OPTIONS;
    },
    budgetCurrency() {
      return this.organization.currency?.symbol || '$';
    },
    budgetLimit() {
      return BUDGET_LIMIT;
    },
    typeSelect2Options() {
      return { ...this.defaultSelect2Options, ...{ placeholder: 'Select a type', allowClear: this.canClearFields } };
    },
    rateSelect2Options() {
      return { ...this.defaultSelect2Options, ...{ placeholder: 'Select a rate', allowClear: this.canClearFields } };
    },
    changedFields() {
      const budget = omit(pick(this.budgetState, BUDGET_FORM_KEYS), ['include_non_billable', 'alert', 'notification']);

      if (!Object.keys(budget).length) {
        return false;
      }

      return Object.values(budget).some((k) => k) || (this.hasBudget && !this.clearedFields);
    },
    requiredField() {
      return this.changedFields ? 'required' : '';
    },
    canClearFields() {
      return !this.hasBudget;
    },
    canClearBudget() {
      if (this.hasBudget) {
        const budget = this.budgetState;

        return (
          budget &&
          ([BUDGET_TYPE_COST, BUDGET_TYPE_HOURS].includes(parseInt(budget.type)) ||
            [BUDGET_USE_BILL_RATE, BUDGET_USE_PAY_RATE].includes(parseInt(budget.rate)) ||
            budget.cost ||
            budget.hours ||
            budget.start ||
            budget.recurrence)
        );
      }

      return false;
    },
    budgetNotCleared() {
      return this.budgetState !== null;
    },
    formattedPlaceholder() {
      return this.durationFormat === 'decimal' ? '0.00' : '0:00';
    },
    requiredFieldsPresent() {
      const nonEmptyBudgetType = this.budgetType !== null;
      const nonEmptyBudgetRateAndCost = this.budgetRate !== null && this.budgetCost !== null;
      const nonEmptyBudgetHours = this.budgetHours !== null;

      return nonEmptyBudgetType && (nonEmptyBudgetRateAndCost || nonEmptyBudgetHours);
    },
    isBudgetTypeHours() {
      return String(this.budgetType) === String(BUDGET_TYPE_HOURS);
    },
  },
  watch: {
    errors: {
      handler(newErrors) {
        this.$emit('errors-changed', newErrors);
      },
    },
  },
  methods: {
    handleChangeBudgetField(field, val) {
      if (val === undefined) {
        this.$emit('change-budget-field', field);
      } else {
        this.$emit('change-budget-field', field, val);
      }
    },
  },
};
</script>
